'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  X,
  CheckCircle2,
  AlertTriangle,
  Wifi,
  Info,
  UserCheck,
  Check,
  Loader2,
  ArrowUpRight,
  CreditCard,
  Zap,
  MapPin,
  User,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';
import { Notification, useNotifications } from '@/lib/context/NotificationContext';

/* ─── Ícono semántico por tipo ──────────────────────────────────────────── */
function TypeIcon({ type }: { type: Notification['type'] }) {
  const sz = 16;
  switch (type) {
    case 'success': return <CheckCircle2  size={sz} />;
    case 'error':   return <AlertTriangle size={sz} />;
    case 'warning': return <Wifi          size={sz} />;
    default:        return <Info          size={sz} />;
  }
}

const TYPE_STYLES: Record<Notification['type'], { bg: string; color: string; border: string }> = {
  success: { bg: 'var(--success-light)', color: 'var(--success)',  border: 'var(--success-light)' },
  error:   { bg: 'var(--danger-light)',  color: 'var(--danger)',   border: 'var(--danger-light)' },
  warning: { bg: 'var(--warning-light)', color: 'var(--warning)',  border: 'var(--warning-light)' },
  info:    { bg: 'var(--bg-elevated)',   color: 'var(--señal)',    border: 'var(--border-strong)'},
};

/* ─── Componente Toast Individual ───────────────────────────────────────── */
function ToastItem({ toast, onRemove }: { toast: Notification, onRemove: (id: string) => void }) {
  const [closing, setClosing] = useState(false);
  const ts = TYPE_STYLES[toast.type];

  // Iniciar cierre manual
  const handleClose = () => {
    setClosing(true);
    setTimeout(() => onRemove(toast.id), 250); // Tiempo de la animación out
  };

  return (
    <div
      onClick={() => {
        if (toast.metadata?.conversationId) {
          window.location.href = '/apps/chat';
        }
      }}
      style={{
        display:        'flex',
        alignItems:     'flex-start',
        gap:            12,
        width:          340,
        background:     'var(--bg-surface)',
        border:         '1px solid var(--border)',
        borderRadius:   'var(--radius-xl)',
        boxShadow:      'var(--shadow-xl)',
        padding:        '14px 16px',
        pointerEvents:  'auto',
        animation:      closing ? 'toastOut 0.25s ease-in forwards' : 'toastIn 0.35s cubic-bezier(0.2, 0.9, 0.3, 1)',
        cursor:         toast.metadata?.conversationId ? 'pointer' : 'default',
      }}
    >
      {/* ── Ícono ── */}
      <div
        style={{
          width:          32,
          height:         32,
          borderRadius:   10,
          background:     ts.bg,
          color:          ts.color,
          border:         `1px solid ${ts.border}`,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          flexShrink:     0,
        }}
      >
        <TypeIcon type={toast.type} />
      </div>

      {/* ── Contenido ── */}
      <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
        <p style={{
          fontSize:   14,
          fontWeight: 600,
          color:      'var(--text-primary)',
          margin:     '0 0 4px',
          lineHeight: 1.2,
        }}>
          {toast.title}
        </p>
        <p style={{
          fontSize:   13,
          color:      'var(--text-secondary)',
          margin:     0,
          lineHeight: 1.4,
        }}>
          {toast.message}
        </p>
        
        {/* Barra de tiempo de vida (opcional, visualiza los 2 mins) */}
        <div style={{
          marginTop: 10,
          height: 3,
          background: 'var(--bg-elevated)',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            background: ts.color,
            animation: 'toastProgress 5s linear forwards' // 5s = 5 segundos
          }} />
        </div>
      </div>

      {/* ── Cerrar ── */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        style={{
          width:          24,
          height:         24,
          borderRadius:   6,
          background:     'transparent',
          border:         'none',
          color:          'var(--text-muted)',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          cursor:         'pointer',
          flexShrink:     0,
          marginTop:      -2,
          marginRight:    -4,
          transition:     'background 0.15s, color 0.15s',
        }}
        onMouseEnter={e => { 
          const el = e.currentTarget as HTMLElement;
          el.style.background = 'var(--bg-elevated)';
          el.style.color = 'var(--text-primary)';
        }}
        onMouseLeave={e => { 
          const el = e.currentTarget as HTMLElement;
          el.style.background = 'transparent';
          el.style.color = 'var(--text-muted)';
        }}
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

/* ─── Componente Toast para Solicitud de Atención (Interactivo) ────────── */
function ClaimRequestToastItem({ toast, onRemove }: { toast: Notification, onRemove: (id: string) => void }) {
  const [loadingAction, setLoadingAction] = useState<'APPROVE' | 'REJECT' | null>(null);
  const { accessToken } = useNotifications();

  const handleRespond = async (action: 'APPROVE' | 'REJECT') => {
    setLoadingAction(action);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const res = await fetch(`/api/chats/${toast.metadata?.conversationId}/respond-request-claim`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          requesterAgentId: toast.metadata?.requesterAgentId,
          action
        })
      });

      if (!res.ok) {
        console.error('Error respondiendo a la solicitud de atención');
      }
    } catch (err) {
      console.error('Error enviando respuesta de atención:', err);
    } finally {
      setLoadingAction(null);
      onRemove(toast.id);
    }
  };

  return (
    <div className="w-[360px] bg-white dark:bg-[#1E293B] rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-[0_12px_45px_rgba(15,23,42,0.22)] p-4 flex flex-col gap-3 animate-in fade-in slide-in-from-right-10 duration-200 pointer-events-auto">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 border border-amber-200/60 dark:border-amber-900/40 shadow-xs">
          <UserCheck size={18} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 px-2 py-0.5 rounded-full border border-amber-200/50 dark:border-amber-900/50">
              Solicitud de Atención
            </span>
            <button
              onClick={() => onRemove(toast.id)}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 rounded-md cursor-pointer"
            >
              <X size={14} />
            </button>
          </div>

          <h4 className="text-xs font-bold text-slate-800 dark:text-white mt-1.5 leading-snug">
            {toast.metadata?.requesterAgentName || 'Un operador'} desea atender este chat
          </h4>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
            Cliente: <span className="font-semibold text-slate-700 dark:text-slate-300">{toast.metadata?.clientName || toast.metadata?.clientPhone}</span>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800/80">
        <button
          type="button"
          disabled={loadingAction !== null}
          onClick={() => handleRespond('REJECT')}
          className="px-3.5 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-xl font-bold text-xs shadow-sm transition-all active:scale-95 flex items-center gap-1 cursor-pointer disabled:opacity-50"
        >
          {loadingAction === 'REJECT' ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
          Rechazar Solicitud
        </button>

        <button
          type="button"
          disabled={loadingAction !== null}
          onClick={() => handleRespond('APPROVE')}
          className="px-4 py-1.5 bg-[var(--señal)] hover:bg-[var(--señal-hover)] text-white rounded-xl font-bold text-xs shadow-md shadow-blue-500/10 transition-all active:scale-95 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
        >
          {loadingAction === 'APPROVE' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          Aprobar Solicitud
        </button>
      </div>
    </div>
  );
}

/* ─── Componente Toast de Pago Webhook Elegante y Moderno (/impeccable) ─── */
function PaymentWebhookToastItem({
  toast,
  onRemove,
}: {
  toast: Notification;
  onRemove: (id: string) => void;
}) {
  const router = useRouter();
  const [closing, setClosing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const data = toast.metadata?.paymentData || {};
  const isAssignedToCurrentUser = Boolean(toast.metadata?.isAssignedToCurrentUser);

  // Sintetizador de audio sutil (tono adaptativo para éxito vs rechazo/declinación vía Web Audio API)
  useEffect(() => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;
      const estadoUpper = String(data.estado || 'APROBADO').toUpperCase();
      const esRechazado = ['RECHAZADO', 'DECLINADO', 'FALLIDO', 'FAILED', 'DECLINED'].includes(estadoUpper);

      if (esRechazado) {
        // Tono suave descendente de advertencia para rechazo/declinación
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(392.0, now); // G4
        osc1.frequency.exponentialRampToValueAtTime(329.63, now + 0.18);
        gain1.gain.setValueAtTime(0.045, now);
        gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.start(now);
        osc1.stop(now + 0.22);

        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(329.63, now + 0.1);
        osc2.frequency.exponentialRampToValueAtTime(261.63, now + 0.35);
        gain2.gain.setValueAtTime(0.04, now + 0.1);
        gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start(now + 0.1);
        osc2.stop(now + 0.4);
      } else {
        // Sintetizador de campana de éxito (D5 -> A5)
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(587.33, now); // D5
        gain1.gain.setValueAtTime(0.05, now);
        gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.start(now);
        osc1.stop(now + 0.3);

        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(880.0, now + 0.08); // A5
        gain2.gain.setValueAtTime(0.05, now + 0.08);
        gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start(now + 0.08);
        osc2.stop(now + 0.5);
      }
    } catch (_) {}
  }, [data.estado]);

  // Timer auto dismiss (8s) con pausa al pasar el cursor (hover)
  useEffect(() => {
    if (isPaused || closing) return;
    const timer = setTimeout(() => {
      handleClose();
    }, 8000);
    return () => clearTimeout(timer);
  }, [isPaused, closing]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => onRemove(toast.id), 250);
  };

  const pasarelaUpper = String(data.pasarela || 'ONLINE').toUpperCase();
  const estadoUpper = String(data.estado || 'APROBADO').toUpperCase();
  const montoNum = Number(data.monto || 0);

  // Paleta de colores e insignias específicas por pasarela de recaudo
  let pasarelaBadge = {
    nombre: 'BOLD',
    bg: 'bg-rose-50 dark:bg-rose-950/60',
    text: 'text-rose-600 dark:text-rose-400',
    border: 'border-rose-200/60 dark:border-rose-900/40',
    icon: <Zap size={12} className="text-rose-500 fill-rose-500" />,
    gradient: 'from-rose-500 via-pink-500 to-rose-600',
  };

  if (pasarelaUpper.includes('WOMPI')) {
    pasarelaBadge = {
      nombre: 'WOMPI',
      bg: 'bg-indigo-50 dark:bg-indigo-950/60',
      text: 'text-indigo-600 dark:text-indigo-400',
      border: 'border-indigo-200/60 dark:border-indigo-900/40',
      icon: <ShieldCheck size={12} className="text-indigo-500" />,
      gradient: 'from-indigo-500 via-purple-500 to-indigo-600',
    };
  } else if (pasarelaUpper.includes('BANCOLOMBIA')) {
    const convRegexMatch = (pasarelaUpper + ' ' + String(data.metodo || '')).match(/CONV\s*([0-9a-zA-Z_-]+)/i);
    const codConv = String(data.codigoConvenio || data.codigo_convenio || data.referencia_2 || (toast as any).datos?.codigoConvenio || (convRegexMatch ? convRegexMatch[1] : '')).trim();
    let nombreBadge = 'BANCOLOMBIA CONV';
    if (codConv) {
      nombreBadge = `BANCOLOMBIA CONV ${codConv}`;
    } else if (pasarelaUpper.includes('CONV')) {
      nombreBadge = pasarelaUpper;
    }
    pasarelaBadge = {
      nombre: nombreBadge,
      bg: 'bg-amber-50 dark:bg-amber-950/60',
      text: 'text-amber-700 dark:text-amber-400',
      border: 'border-amber-200/60 dark:border-amber-900/40',
      icon: <CheckCircle2 size={12} className="text-amber-600" />,
      gradient: 'from-amber-500 via-yellow-500 to-amber-600',
    };
  } else if (pasarelaUpper !== 'BOLD') {
    pasarelaBadge = {
      nombre: pasarelaUpper,
      bg: 'bg-sky-50 dark:bg-sky-950/60',
      text: 'text-sky-600 dark:text-sky-400',
      border: 'border-sky-200/60 dark:border-sky-900/40',
      icon: <CreditCard size={12} className="text-sky-500" />,
      gradient: 'from-sky-500 via-blue-500 to-sky-600',
    };
  }

  // Estado del pago
  const esAprobado = estadoUpper === 'APROBADO' || estadoUpper === 'CONCILIADO';
  const esRechazado = ['RECHAZADO', 'DECLINADO', 'FALLIDO', 'FAILED', 'DECLINED'].includes(estadoUpper);

  const estadoBadgeClass = esAprobado
    ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-900/40'
    : esRechazado
    ? 'bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border-rose-200/60 dark:border-rose-900/40'
    : 'bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border-amber-200/60 dark:border-amber-900/40';

  const estadoLabel = esAprobado
    ? 'Acreditado'
    : esRechazado
    ? (estadoUpper === 'DECLINADO' ? 'Declinado' : 'Rechazado')
    : 'Por Conciliar';

  const clienteNombre = data.cliente?.nombre || 'Cliente';
  const clienteDoc = data.cliente?.documento;
  const sedeNombre = data.cliente?.sede_nombre;
  const usuarioAsignadoNombre = data.cliente?.usuario_asignado_nombre || data.solicitud?.solicitado_por_nombre;
  const numeroFactura = data.factura?.numero_factura;
  const referenciaCanonica = data.referencia || data.pagoId;
  const metodoPago = data.metodo || 'ONLINE';

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className={`relative w-[385px] max-w-[calc(100vw-32px)] bg-white dark:bg-[#1E293B] rounded-2xl border border-slate-200/90 dark:border-slate-800/90 shadow-[0_20px_50px_rgba(15,23,42,0.22)] p-4 flex flex-col gap-3 pointer-events-auto overflow-hidden group transition-all duration-200 ${
        closing
          ? 'animate-out fade-out slide-out-to-right-10 duration-200'
          : 'animate-in fade-in slide-in-from-right-10 duration-300'
      }`}
    >
      {/* ── Borde superior con acento degradado (adaptativo para rechazo) ── */}
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${esRechazado ? 'from-rose-500 via-amber-500 to-rose-600' : pasarelaBadge.gradient}`} />

      {/* ── Fila de Encabezado: Pasarela, Estado en vivo y Botón Cerrar ── */}
      <div className="flex items-center justify-between pt-0.5">
        <div className="flex items-center gap-2">
          {/* Badge Pasarela */}
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider border ${pasarelaBadge.bg} ${pasarelaBadge.text} ${pasarelaBadge.border}`}>
            {pasarelaBadge.icon}
            {pasarelaBadge.nombre}
          </span>

          {/* Badge de Estado con Pulsador */}
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${estadoBadgeClass}`}>
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${esAprobado ? 'bg-emerald-400' : esRechazado ? 'bg-rose-400' : 'bg-amber-400'}`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${esAprobado ? 'bg-emerald-500' : esRechazado ? 'bg-rose-500' : 'bg-amber-500'}`}></span>
            </span>
            {estadoLabel}
          </span>
        </div>

        <button
          type="button"
          onClick={handleClose}
          aria-label="Cerrar notificación"
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
        >
          <X size={15} />
        </button>
      </div>

      {/* ── Fila del Cliente y Sede Asignada ── */}
      <div className="flex items-start gap-3">
        {/* Avatar / Iniciales */}
        <div className={`w-10 h-10 rounded-xl ${
          esRechazado
            ? 'bg-gradient-to-br from-rose-500 to-amber-600 shadow-rose-500/20'
            : 'bg-gradient-to-br from-[var(--señal)] to-emerald-600 shadow-blue-500/10'
        } text-white font-black text-sm flex items-center justify-center shrink-0 shadow-sm`}>
          {clienteNombre.charAt(0).toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="text-[13.5px] font-extrabold text-slate-800 dark:text-white leading-tight truncate">
            {clienteNombre}
          </h4>

          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
            {clienteDoc && (
              <span className="font-mono text-slate-600 dark:text-slate-300 font-medium">
                CC {clienteDoc}
              </span>
            )}
            {numeroFactura && (
              <span className="inline-flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-300">
                • {numeroFactura}
              </span>
            )}
          </div>

          {/* Badges de Sede y Operador Asignado */}
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            {sedeNombre && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/60">
                <MapPin size={10} className="text-slate-400" />
                {sedeNombre}
              </span>
            )}

            {isAssignedToCurrentUser ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 border border-blue-200/60 dark:border-blue-900/40">
                <User size={10} />
                Asignado a ti
              </span>
            ) : usuarioAsignadoNombre ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                <User size={10} className="text-slate-400" />
                {usuarioAsignadoNombre}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* ── Cápsula Financiera Destacada ── */}
      <div className={`${
        esRechazado
          ? 'bg-rose-50/70 dark:bg-rose-950/30 border-rose-200/60 dark:border-rose-900/40'
          : 'bg-slate-50/90 dark:bg-slate-900/80 border-slate-100 dark:border-slate-800/70'
      } rounded-xl p-3 border flex items-center justify-between`}>
        <div>
          <span className={`text-[10px] font-bold uppercase tracking-wider ${
            esRechazado ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400 dark:text-slate-500'
          } block leading-none`}>
            {esRechazado ? 'Monto No Acreditado' : 'Monto Recaudado'}
          </span>
          <span className={`text-[17px] font-extrabold ${
            esRechazado ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'
          } font-mono tracking-tight mt-1 block`}>
            ${montoNum.toLocaleString('es-CO')}{' '}
            <span className="text-[10px] font-bold text-slate-400">COP</span>
          </span>
        </div>

        <div className="text-right flex flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200/60 dark:border-slate-700/60 shadow-xs">
            <CreditCard size={11} className={esRechazado ? 'text-rose-500' : 'text-blue-500'} />
            {metodoPago}
          </span>
          <span className="text-[10px] text-slate-400 font-mono truncate max-w-[130px]" title={referenciaCanonica}>
            {referenciaCanonica}
          </span>
        </div>
      </div>

      {/* Motivo de rechazo detallado si existe */}
      {esRechazado && data.motivoRechazo && (
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-rose-50 dark:bg-rose-950/40 border border-rose-200/60 dark:border-rose-900/30 text-[11px] text-rose-700 dark:text-rose-300">
          <AlertTriangle size={12} className="shrink-0 text-rose-500" />
          <span className="truncate">{data.motivoRechazo}</span>
        </div>
      )}

      {/* ── Footer de Acciones ── */}
      <div className="flex items-center justify-between pt-1 border-t border-slate-100 dark:border-slate-800/80">
        <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 flex items-center gap-1">
          {esRechazado ? (
            <>
              <AlertTriangle size={12} className="text-rose-500" />
              Transacción no acreditada
            </>
          ) : (
            <>
              <Sparkles size={12} className="text-amber-500" />
              Procesado en tiempo real
            </>
          )}
        </span>

        <button
          type="button"
          onClick={() => {
            handleClose();
            router.push(data.solicitud?.id ? '/pagos/solicitudes' : '/pagos/verificacionfac');
          }}
          className={`px-3.5 py-1.5 ${
            esRechazado
              ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-rose-500/15'
              : 'bg-[var(--señal)] hover:bg-[var(--señal-hover)] text-white shadow-blue-500/10'
          } rounded-xl font-bold text-xs shadow-md transition-all active:scale-95 flex items-center gap-1.5 cursor-pointer`}
        >
          <span>{data.solicitud?.id ? 'Ver Solicitud' : 'Ver en Pagos'}</span>
          <ArrowUpRight size={13} />
        </button>
      </div>

      {/* ── Barra de Progreso Auto-Dismiss (con pausa al pasar el cursor) ── */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div
          className={`h-full ${esAprobado ? 'bg-emerald-500' : esRechazado ? 'bg-rose-500' : 'bg-amber-500'} transition-all`}
          style={{
            animation: 'paymentToastProgress 8s linear forwards',
            animationPlayState: isPaused ? 'paused' : 'running',
          }}
        />
      </div>
    </div>
  );
}


/* ─── Contenedor Global de Toasts ───────────────────────────────────────── */
export default function NotificationToasts() {
  const { toasts, removeToast } = useNotifications();

  if (toasts.length === 0) return null;

  return (
    <>
      <div
        style={{
          position:      'fixed',
          bottom:        24,
          right:         24,
          zIndex:        9999,
          display:       'flex',
          flexDirection: 'column',
          gap:           12,
          pointerEvents: 'none', // Para que los clicks pasen a través del contenedor
          alignItems:    'flex-end',
        }}
      >
        {toasts.map(toast => 
          toast.metadata?.isClaimRequest ? (
            <ClaimRequestToastItem key={toast.id} toast={toast} onRemove={removeToast} />
          ) : toast.metadata?.isPaymentWebhook ? (
            <PaymentWebhookToastItem key={toast.id} toast={toast} onRemove={removeToast} />
          ) : (
            <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
          )
        )}
      </div>

      <style>{`
        @keyframes toastIn {
          0% { opacity: 0; transform: translateX(40px) scale(0.95); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes toastOut {
          0% { opacity: 1; transform: translateX(0) scale(1); }
          100% { opacity: 0; transform: translateX(20px) scale(0.95); }
        }
        @keyframes toastProgress {
          from { width: 100%; }
          to { width: 0%; }
        }
        @keyframes paymentToastProgress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </>
  );
}

